/**
 * TranscriptStreamClient (WS-01)
 *
 * Owns the live transcript WebSocket for one recording session: token
 * handling, reconnect with backfill, the in-memory segment store the
 * panel renders, and clean stop ordering.
 *
 * Module-scoped singleton, NOT component state — the meetings modal can
 * close (unmounting every meeting component) while recording continues
 * in MeetingContext; the socket and transcript history must survive
 * that and re-render intact when the modal reopens.
 *
 * Display-only failure model: any terminal failure here leaves the
 * recording pipeline completely untouched.
 */

import {
    ensureFreshAuthToken,
    getLiveTranscript,
    websocket_transcript_endpoint,
} from "../service/apiService";
import { LiveTranscriptSegment, TranscriptStreamStatus } from "../types";

const PING_INTERVAL_MS = 20_000; // survives LB idle timeouts; 3x margin vs server's 60 s staleness
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_BASE_DELAY_MS = 1_000;
// After the fast budget (1+2+4 s) is spent, keep trying slowly for the
// rest of the recording. A 2-hour session must not lose its live
// transcript to one 10-second outage, and the recording itself gives no
// other signal to retry on. Status stays "interrupted" between
// attempts, so the panel keeps telling the truth.
const SLOW_RECONNECT_DELAY_MS = 30_000;
// Server refusals no retry can fix (see the router's close codes).
// 4409 (conflict) is deliberately NOT here: the predecessor socket may
// simply not be reaped yet, which is exactly what a retry cures.
const TERMINAL_CLOSE_CODES = new Set([4403, 4404]);
const STOP_COMPLETION_TIMEOUT_MS = 8_000;
// Browser->server backpressure hysteresis: raise the stalled flag when
// the socket's unsent buffer grows past STALL, clear when it drains
// below CLEARED. Catches short outages that never close the socket.
const STALL_BUFFERED_BYTES = 50_000;
const STALL_CLEARED_BUFFERED_BYTES = 8_000;
// Largest PCM frame we put in ONE audio message. The recorder's
// ScriptProcessor hands us 4096 samples (256 ms) at a time, but an audio
// event that large intermittently wedges Transcribe's HTTP/2 request
// body: the awscrt writer stops draining after ~64 KB (8 such events),
// audio silently stops reaching AWS while send() still succeeds, and
// 15 s later the stream dies with "no new audio was received for 15
// seconds". Splitting into ~100 ms pieces — the size raedbora-ai streams
// in production on the same SDK — keeps every event well inside the
// initial flow-control window. Never raise this.
const MAX_PCM_SAMPLES_PER_MESSAGE = 1_600; // 100 ms @ 16 kHz

type Listener = () => void;

function int16ToBase64(pcm: Int16Array): string {
    const bytes = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
    let binary = "";
    // Chunked — String.fromCharCode.apply on the whole frame can blow the
    // call stack on some engines.
    const STEP = 0x8000;
    for (let i = 0; i < bytes.length; i += STEP) {
        binary += String.fromCharCode.apply(
            null,
            Array.from(bytes.subarray(i, i + STEP))
        );
    }
    return btoa(binary);
}

class TranscriptStreamClient {
    private ws: WebSocket | null = null;
    private sessionId: string | null = null;
    private status: TranscriptStreamStatus = "idle";
    private stalled = false;

    private segments: LiveTranscriptSegment[] = [];
    private segmentIndexById: Map<string, number> = new Map();

    private listeners: Set<Listener> = new Set();
    private pingTimer: ReturnType<typeof setInterval> | null = null;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private reconnectAttempts = 0;
    private stopping = false;
    private paused = false;
    // resume() arrived while pause() was still waiting on its stop
    // handshake. pause() owns the socket for that window, so it has to
    // honour the resume when it lands — otherwise the socket stays down
    // for the rest of the recording with nothing scheduled to reopen it.
    private pendingResume = false;
    private completionResolve: (() => void) | null = null;

    // The session's elapsed clock (ms since recording start). Transcribe
    // offsets restart at zero on every new stream, so both the server
    // (persistence) and this client (display) rebase onto this.
    private elapsedMsProvider: () => number = () => 0;
    private connectionBaseMs = 0;

    // ------------------------------------------------------------ lifecycle

    async start(sessionId: string, elapsedMsProvider: () => number): Promise<void> {
        if (!websocket_transcript_endpoint) {
            this.sessionId = sessionId;
            this.setStatus("unavailable");
            return;
        }
        this.sessionId = sessionId;
        this.elapsedMsProvider = elapsedMsProvider;
        this.stopping = false;
        this.paused = false;
        this.pendingResume = false;
        this.reconnectAttempts = 0;
        this.segments = [];
        this.segmentIndexById.clear();
        await this.connect();
    }

    /**
     * Clean stop: tell the server, wait (bounded) for the completion
     * frame so the post-close tail segments land, then tear down.
     * Call BEFORE finalize — the server writes the transcript during
     * this exchange.
     */
    async stop(): Promise<void> {
        this.stopping = true;
        this.paused = false;
        this.clearReconnect();
        await this.handshakeStop();
        this.teardownSocket();
        if (this.status !== "unavailable") {
            this.setStatus("completed");
        }
    }

    /**
     * Suspend the live stream for a recording pause.
     *
     * A pause sends no audio, and Transcribe kills a stream ~15 s after
     * the last audio event — so leaving the socket parked open doesn't
     * "keep" the stream, it just makes AWS tear it down as a failure and
     * sends the client into a reconnect loop that starves the same way.
     * A real close is the only correct suspend: stop cleanly (the server
     * drains its tail finals and persists), then reopen on resume.
     */
    async pause(): Promise<void> {
        if (this.status === "unavailable" || !this.sessionId) return;
        if (this.paused) return;
        this.paused = true;
        this.pendingResume = false;
        this.clearReconnect();
        // Suppress the reconnect that our own close would otherwise
        // schedule; cleared again once the socket is down.
        this.stopping = true;
        await this.handshakeStop();
        this.teardownSocket();
        this.stopping = false;
        if (this.pendingResume) {
            // The user resumed during the handshake (it waits up to 8 s
            // for the server's completion frame). Reconnect instead of
            // parking, or live transcript is dead until the next full
            // pause/resume cycle.
            this.pendingResume = false;
            this.paused = false;
            await this.reconnectNow();
            return;
        }
        this.setStatus("paused");
    }

    /** Reopen after pause(). Display-only, so failure is harmless. */
    async resume(): Promise<void> {
        if (!this.paused) return;
        if (this.stopping) {
            // pause() still owns the socket; it reconnects for us.
            this.pendingResume = true;
            return;
        }
        this.paused = false;
        await this.reconnectNow();
    }

    /** Tell the server to stop and wait (bounded) for its completion
     * frame, so the post-close tail segments land. No-op when there is
     * no open socket. */
    private async handshakeStop(): Promise<void> {
        const ws = this.ws;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        const completed = new Promise<void>((resolve) => {
            this.completionResolve = resolve;
            setTimeout(resolve, STOP_COMPLETION_TIMEOUT_MS);
        });
        try {
            ws.send(JSON.stringify({ type: "stop" }));
        } catch {
            // fall through to teardown
        }
        await completed;
    }

    /**
     * Force a fresh connection attempt with the retry budget reset.
     * Used on recording resume: a long pause sends no audio, so the
     * server may close the stream for staleness and the normal retry
     * budget can be exhausted while paused. No-op if already connected
     * or stopping.
     */
    async reconnectNow(): Promise<void> {
        if (this.stopping || this.paused || !this.sessionId) return;
        if (this.status === "unavailable") return;
        if (this.ws && this.ws.readyState === WebSocket.OPEN) return;
        this.clearReconnect();
        this.teardownSocket();
        this.reconnectAttempts = 0;
        await this.backfill();
        await this.connect();
    }

    /** Immediate teardown with no stop handshake (session aborted). */
    abort(): void {
        this.stopping = true;
        this.paused = false;
        this.pendingResume = false;
        this.clearReconnect();
        this.teardownSocket();
        this.sessionId = null;
        this.setStatus("idle");
    }

    /** Clear the transcript store (next session starts clean). */
    reset(): void {
        this.abort();
        this.segments = [];
        this.segmentIndexById.clear();
        this.stalled = false;
        this.notify();
    }

    // ---------------------------------------------------------------- audio

    sendPcm(pcm: Int16Array): void {
        const ws = this.ws;
        if (!ws || ws.readyState !== WebSocket.OPEN || this.stopping) return;
        try {
            // One recorder frame becomes N ~100 ms audio messages — see
            // MAX_PCM_SAMPLES_PER_MESSAGE. Order is preserved, so the
            // server-side relay is unchanged.
            for (let i = 0; i < pcm.length; i += MAX_PCM_SAMPLES_PER_MESSAGE) {
                const slice = pcm.subarray(i, i + MAX_PCM_SAMPLES_PER_MESSAGE);
                ws.send(
                    JSON.stringify({ type: "audio", data: int16ToBase64(slice) })
                );
            }
        } catch {
            return;
        }
        const buffered = ws.bufferedAmount;
        if (!this.stalled && buffered > STALL_BUFFERED_BYTES) {
            this.stalled = true;
            this.notify();
        } else if (this.stalled && buffered < STALL_CLEARED_BUFFERED_BYTES) {
            this.stalled = false;
            this.notify();
        }
    }

    // ------------------------------------------------------------ consumers

    subscribe(listener: Listener): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    getSegments(): LiveTranscriptSegment[] {
        return this.segments;
    }

    getStatus(): TranscriptStreamStatus {
        return this.status;
    }

    isStalled(): boolean {
        return this.stalled;
    }

    // ------------------------------------------------------------- internal

    private async connect(): Promise<void> {
        this.setStatus(this.reconnectAttempts > 0 ? "reconnecting" : "connecting");

        // Raw WebSockets bypass the axios re-auth interceptor: refresh
        // BEFORE opening, and again on every reconnect attempt.
        let token: string | null = null;
        try {
            token = await ensureFreshAuthToken();
        } catch {
            token = null;
        }
        if (!token || !this.sessionId) {
            this.handleConnectionFailure();
            return;
        }

        const base = websocket_transcript_endpoint!.endsWith("/")
            ? websocket_transcript_endpoint!
            : `${websocket_transcript_endpoint}/`;
        const url = `${base}${this.sessionId}?token=${encodeURIComponent(token)}`;

        let ws: WebSocket;
        try {
            ws = new WebSocket(url);
        } catch {
            this.handleConnectionFailure();
            return;
        }
        this.ws = ws;
        this.connectionBaseMs = this.elapsedMsProvider();

        ws.onopen = () => {
            if (ws !== this.ws) return;
            ws.send(
                JSON.stringify({
                    type: "start",
                    data: { elapsed_ms: Math.max(0, Math.round(this.connectionBaseMs)) },
                })
            );
            this.startPing();
        };

        ws.onmessage = (event: MessageEvent) => {
            if (ws !== this.ws) return;
            this.handleFrame(event.data);
        };

        ws.onerror = () => {
            // onclose always follows; reconnect logic lives there.
        };

        ws.onclose = (event: CloseEvent) => {
            // A browser dispatches close asynchronously — an abandoned
            // socket's late close must not tear down its replacement.
            if (ws !== this.ws) return;
            this.stopPing();
            if (this.stopping) return;
            if (TERMINAL_CLOSE_CODES.has(event.code)) {
                // Not ours / gone — retrying can only fail again.
                this.setStatus("interrupted");
                return;
            }
            this.scheduleReconnect();
        };
    }

    private handleFrame(raw: unknown): void {
        let frame: any;
        try {
            frame = JSON.parse(String(raw));
        } catch {
            return;
        }
        switch (frame.type) {
            case "segment": {
                const d = frame.data || {};
                if (typeof d.segment_id !== "string" || typeof d.text !== "string") return;
                this.upsertSegment({
                    id: d.segment_id,
                    text: d.text,
                    isStable: !!d.is_stable,
                    speaker: d.speaker ?? null,
                    absStartMs: this.connectionBaseMs + (d.start_ms || 0),
                    absEndMs: this.connectionBaseMs + (d.end_ms || 0),
                });
                if (this.status !== "live") this.setStatus("live");
                else this.notify();
                break;
            }
            case "event":
                if (frame.data === "started") {
                    this.reconnectAttempts = 0;
                    this.setStatus("live");
                } else if (frame.data === "completion") {
                    this.completionResolve?.();
                    this.completionResolve = null;
                }
                break;
            case "error":
                console.error(
                    "[TranscriptStream] server error frame:",
                    frame.data?.code
                );
                // The server closes after an error frame; onclose decides
                // between reconnect and interrupted.
                break;
            default:
                break;
        }
    }

    private upsertSegment(segment: LiveTranscriptSegment): void {
        const existing = this.segmentIndexById.get(segment.id);
        if (existing !== undefined) {
            this.segments[existing] = segment;
        } else {
            this.segmentIndexById.set(segment.id, this.segments.length);
            this.segments.push(segment);
        }
        // New array reference so React subscribers see the change.
        this.segments = [...this.segments];
        this.reindex();
    }

    private reindex(): void {
        this.segmentIndexById.clear();
        this.segments.forEach((s, i) => this.segmentIndexById.set(s.id, i));
    }

    private scheduleReconnect(): void {
        if (this.paused) return;
        if (this.reconnectTimer) return;
        // Fast budget first, then keep trying slowly for as long as the
        // recording runs — never stop retrying outright.
        const exhausted = this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS;
        const delay = exhausted
            ? SLOW_RECONNECT_DELAY_MS
            : RECONNECT_BASE_DELAY_MS * 2 ** this.reconnectAttempts;
        this.reconnectAttempts += 1;
        this.setStatus(exhausted ? "interrupted" : "reconnecting");
        this.reconnectTimer = setTimeout(async () => {
            this.reconnectTimer = null;
            if (this.stopping || this.paused) return;
            await this.backfill();
            await this.connect();
        }, delay);
    }

    /**
     * Merge the server-persisted transcript into the local store —
     * covers segments finalized while the socket was down, and the
     * page-reload case where the local store starts empty. Keyed by
     * segment_id, so double delivery is harmless.
     */
    private async backfill(): Promise<void> {
        if (!this.sessionId) return;
        try {
            const data = await getLiveTranscript(this.sessionId);
            for (const s of data.segments || []) {
                if (typeof s.segment_id !== "string") continue;
                this.upsertSegment({
                    id: s.segment_id,
                    text: s.text || "",
                    isStable: true,
                    speaker: s.speaker ?? null,
                    absStartMs: s.abs_start_ms || 0,
                    absEndMs: s.abs_end_ms || 0,
                });
            }
            // Persisted order is authoritative for restored content.
            this.segments = [...this.segments].sort(
                (a, b) => a.absStartMs - b.absStartMs
            );
            this.reindex();
            this.notify();
        } catch {
            // Backfill is best-effort; live resumption matters more.
        }
    }

    private handleConnectionFailure(): void {
        if (this.stopping) return;
        this.scheduleReconnect();
    }

    private startPing(): void {
        this.stopPing();
        this.pingTimer = setInterval(() => {
            const ws = this.ws;
            if (ws && ws.readyState === WebSocket.OPEN) {
                try {
                    ws.send(JSON.stringify({ type: "ping" }));
                } catch {
                    // close handler owns recovery
                }
            }
        }, PING_INTERVAL_MS);
    }

    private stopPing(): void {
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
    }

    private clearReconnect(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    private teardownSocket(): void {
        this.stopPing();
        const ws = this.ws;
        this.ws = null;
        if (ws) {
            ws.onopen = null;
            ws.onmessage = null;
            ws.onerror = null;
            ws.onclose = null;
            try {
                ws.close();
            } catch {
                // already closed
            }
        }
    }

    private setStatus(status: TranscriptStreamStatus): void {
        this.status = status;
        this.notify();
    }

    private notify(): void {
        this.listeners.forEach((listener) => {
            try {
                listener();
            } catch {
                // a broken subscriber must not break the stream
            }
        });
    }
}

// Module-scoped singleton (see file docstring).
export const transcriptStreamClient = new TranscriptStreamClient();
